import React, { useEffect, useState, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Users, MessageSquare, UserCheck, Clock, TrendingUp, FileSpreadsheet, Send, CheckCircle2, AlertCircle, Crown, Stethoscope, Archive } from "lucide-react";
import { useAdminAuth } from "./AdminAuthContext";
import { processAnalytics } from "./components/AnalyticsCalculators";
import CeoExecutiveOverview from "./components/CeoExecutiveOverview";

const StatCard = ({ icon: Icon, label, value, color, delay, testId }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="relative rounded-2xl border border-white/10 p-6 overflow-hidden group"
    style={{ background: "rgba(10,22,40,0.5)", backdropFilter: "blur(12px)" }}
    data-testid={testId}
  >
    <div
      className="absolute -top-12 -right-12 w-40 h-40 rounded-full blur-3xl opacity-20 group-hover:opacity-40 transition-opacity"
      style={{ background: color }}
    />
    <div className="relative z-10">
      <div className="flex items-center justify-between mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}22`, border: `1px solid ${color}66` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <TrendingUp className="w-4 h-4 text-white/30" />
      </div>
      <div className="text-3xl font-light text-white mb-1" data-testid={`${testId}-value`}>{value}</div>
      <div className="text-xs text-white/50 uppercase tracking-wider">{label}</div>
    </div>
  </motion.div>
);

const AdminDashboard = () => {
  const dashboardRef = useRef(null);
  console.log("DASHBOARD_COMPONENT_RENDER", performance.now());
  console.log(
    "DASHBOARD_DOM",
    document.querySelector('[data-testid="admin-dashboard"]')
  );
  console.log("HAS_DASHBOARD_HTML", document.body.innerHTML.includes("admin-dashboard"));
  console.log("HAS_APPLICATIONS_HTML", document.body.innerHTML.includes("admin-applications"));
  const { api } = useAdminAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState("");
  const [generatingReport, setGeneratingReport] = useState(null); // 'daily' | 'weekly' | 'monthly' | null
  const [reportFeedback, setReportFeedback] = useState(null); // { type: 'success' | 'error', text: string }

  console.debug("[ADMIN DASHBOARD COMPONENT RENDER]", {
    hasStats: !!stats,
    applicationsCount: applications.length,
    hasError: !!error,
    timestamp: new Date().toISOString()
  });

  useEffect(() => {
    console.debug("[ADMIN DASHBOARD USE EFFECT START: FETCH DATA]", { timestamp: new Date().toISOString() });
    const fetchData = async () => {
      try {
        const [statsRes, appsRes] = await Promise.all([
          api().get("/admin/dashboard/stats"),
          api().get("/admin/applications").catch(() => ({ data: [] }))
        ]);
        const appsList = Array.isArray(appsRes.data)
          ? appsRes.data
          : appsRes.data?.applications || [];
        console.debug("[ADMIN DASHBOARD API COMPLETION]", {
          statsSuccess: !!statsRes.data,
          appsCount: appsList.length,
          timestamp: new Date().toISOString()
        });
        setStats(statsRes.data);
        setApplications(appsList);
      } catch (err) {
        console.debug("[ADMIN DASHBOARD API ERROR]", { error: err.message, timestamp: new Date().toISOString() });
        setError("Failed to load dashboard stats");
      }
    };
    fetchData();
  }, [api]);

  const analytics = useMemo(() => {
    return processAnalytics(applications);
  }, [applications]);

  const handleGenerateReport = async (type) => {
    setGeneratingReport(type);
    setReportFeedback(null);
    try {
      const res = await api().post("/admin/reports/generate", { type });
      setReportFeedback({
        type: 'success',
        text: res.data?.message || `Human Capital Intelligence ${type} report generated and dispatched to HR!`
      });
    } catch (err) {
      setReportFeedback({
        type: 'error',
        text: err.response?.data?.detail || `Failed to generate ${type} report.`
      });
    } finally {
      setGeneratingReport(null);
    }
  };

  return (
    <div data-testid="admin-dashboard" ref={dashboardRef}>
      <div className="mb-8">
        <h1 className="text-3xl font-light text-white">Dashboard</h1>
        <p className="text-sm text-cyan-300/60 mt-1">Real-time overview of recruitment & engagement</p>
      </div>

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-300 text-sm">{error}</div>
      )}

      {/* CEO EXECUTIVE OVERVIEW & ENTERPRISE ANALYTICS */}
      <div className="mb-8">
        <CeoExecutiveOverview 
          ceoOverview={analytics.ceoOverview} 
          departmentSummaries={analytics.departmentSummaries} 
          smartAlerts={analytics.smartAlerts} 
          topCandidates={analytics.topCandidates}
          upcomingInterviews={analytics.upcomingInterviews}
          todayKpiSnapshot={analytics.todayKpiSnapshot}
          executiveInsights={analytics.executiveInsights}
          applications={applications}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <StatCard icon={Users} label="Total Applications" value={stats?.total_applications ?? "—"} color="#00FFD1" delay={0.05} testId="stat-total-applications" />
        <StatCard icon={Clock} label="New Applications" value={stats?.new_applications ?? "—"} color="#4facfe" delay={0.1} testId="stat-new-applications" />
        <StatCard icon={UserCheck} label="Shortlisted" value={stats?.shortlisted ?? "—"} color="#43e97b" delay={0.15} testId="stat-shortlisted" />
        <StatCard icon={MessageSquare} label="Total Messages" value={stats?.total_contacts ?? "—"} color="#f5576c" delay={0.2} testId="stat-total-contacts" />
        <StatCard icon={Clock} label="Pending Messages" value={stats?.pending_contacts ?? "—"} color="#fa709a" delay={0.25} testId="stat-pending-contacts" />
      </div>

      {/* HUMAN CAPITAL INTELLIGENCE REPORTING PANEL */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mt-8 p-6 rounded-2xl border border-cyan-500/20"
        style={{ background: "rgba(10,22,40,0.6)", backdropFilter: "blur(12px)" }}
      >
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
              <FileSpreadsheet className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-white">Human Capital Intelligence Reports</h2>
              <p className="text-xs text-white/50">Internal executive management reporting suite (Daily, Weekly, Monthly)</p>
            </div>
          </div>
          <span className="text-[11px] px-3 py-1 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 uppercase tracking-wider font-semibold">
            Auto Schedule: 11:59 PM IST
          </span>
        </div>

        {reportFeedback && (
          <div className={`mb-4 p-3.5 rounded-xl flex items-center gap-3 text-sm border ${
            reportFeedback.type === 'success' 
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' 
              : 'bg-red-500/10 border-red-500/30 text-red-300'
          }`}>
            {reportFeedback.type === 'success' ? <CheckCircle2 className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
            <span>{reportFeedback.text}</span>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
          <button
            onClick={() => handleGenerateReport('daily')}
            disabled={generatingReport !== null}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-xs font-semibold uppercase tracking-wider transition-all disabled:opacity-50 cursor-pointer"
          >
            {generatingReport === 'daily' ? (
              <div className="w-4 h-4 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            <span>Generate Today's Report</span>
          </button>

          <button
            onClick={() => handleGenerateReport('weekly')}
            disabled={generatingReport !== null}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs font-semibold uppercase tracking-wider transition-all disabled:opacity-50 cursor-pointer"
          >
            {generatingReport === 'weekly' ? (
              <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            <span>Generate This Week's Report</span>
          </button>

          <button
            onClick={() => handleGenerateReport('monthly')}
            disabled={generatingReport !== null}
            className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs font-semibold uppercase tracking-wider transition-all disabled:opacity-50 cursor-pointer"
          >
            {generatingReport === 'monthly' ? (
              <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-3.5 h-3.5" />
            )}
            <span>Generate This Month's Report</span>
          </button>
        </div>

        {/* Live Instant Report Previews (HTML / PDF / JSON) */}
        <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between flex-wrap gap-3">
          <span className="text-xs text-white/50">Instant Report Previews:</span>
          <div className="flex items-center gap-2 flex-wrap">
            <a
              href="/api/admin/reports/preview?type=daily"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 rounded-lg bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-xs font-medium transition-all"
            >
              Preview Daily HTML
            </a>
            <a
              href="/api/admin/reports/pdf?type=daily"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-xs font-medium transition-all"
            >
              Export PDF View
            </a>
            <a
              href="/api/admin/reports/json?type=daily"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 rounded-lg bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs font-medium transition-all"
            >
              View JSON Archive
            </a>
          </div>
        </div>
      </motion.div>

      {/* LIGHTWEIGHT SYSTEM DIAGNOSTICS & REPORTS ARCHIVE SUMMARY WIDGETS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        <div className="p-6 rounded-2xl border border-cyan-500/20 bg-slate-900/60 backdrop-blur-md flex flex-col justify-between" data-testid="dashboard-diagnostics-card">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
              <Stethoscope className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-base font-medium text-white">System Diagnostics & Platform Health</h3>
              <p className="text-xs text-white/50 mt-1">Live production health telemetry, API latency checks and system audit suite.</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/admin/diagnostics")}
            className="w-full py-2.5 rounded-xl bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 text-cyan-300 text-xs font-semibold uppercase tracking-wider transition-all cursor-pointer flex items-center justify-center gap-2"
          >
            <span>Launch Diagnostics Console</span>
          </button>
        </div>

        <div className="p-6 rounded-2xl border border-purple-500/20 bg-slate-900/60 backdrop-blur-md flex flex-col justify-between" data-testid="dashboard-reports-card">
          <div className="flex items-start gap-4 mb-4">
            <div className="p-3 rounded-xl bg-purple-500/10 border border-purple-500/30 text-purple-400">
              <Archive className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-base font-medium text-white">Reports Archive Repository</h3>
              <p className="text-xs text-white/50 mt-1">Historical human capital intelligence reports, PDF exports and compliance logs.</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/admin/reports-archive")}
            className="w-full py-2.5 rounded-xl bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs font-semibold uppercase tracking-wider transition-all cursor-pointer flex items-center justify-center gap-2"
          >
            <span>Open Reports Archive</span>
          </button>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-8 p-6 rounded-2xl border border-white/10"
        style={{ background: "rgba(10,22,40,0.5)", backdropFilter: "blur(12px)" }}
      >
        <h2 className="text-lg font-light text-white mb-2">Welcome to the Admin Console</h2>
        <p className="text-sm text-white/60 leading-relaxed">
          Manage job applications, review contact messages, and audit administrative activity from this central console.
          Use the sidebar to navigate between sections. All actions are logged for compliance.
        </p>
      </motion.div>
    </div>
  );
};

export default AdminDashboard;

